<template>
  <ElConfigProvider namespace="el">
    <div class="app-container mflow-editor">
      <!-- Top Panel with toolbar -->
      <TopPanel 
        :showToolbar="showToolbar"
        :leftVisible="leftVisible"
        :bottomVisible="bottomVisible"
        :miniMapVisible="miniMapVisible"
        @toggle-left="toggleLeft"
        @toggle-bottom="toggleBottom"
        @toggle-minimap="toggleMiniMap"
      />
      
      <!-- Left Navigation / Management Panel -->
      <LeftPanel :visible="leftVisible" />

      <!-- 主内容区域：根据左侧面板和底部面板动态留白 -->
      <div class="main-content" :style="mainContentStyle">
        <MFlow 
          :nodes="workflowStore.state.nodes" 
          :edges="workflowStore.state.edges" 
          :nodeManager="nodeManager" 
          :themeManager="themeManager" 
          :backgroundGap="backgroundGap"
          :maxZoom="maxZoom" 
          :minZoom="minZoom" 
          :findNode="workflowStore.findNode" 
          :updateNode="workflowStore.updateNode" 
          :removeNode="workflowStore.removeNode"
          :removeEdge="workflowStore.removeEdge" 
          :moveNode="workflowStore.moveNode" 
          :setActiveNodeId="workflowStore.setActiveNodeId" 
          :addEdge="workflowStore.addEdge"
          :updateNodeData="workflowStore.updateNodeData" 
          :addNode="workflowStore.addNode"
          :nodeDoubleClick="onNodeDoubleClick"
          :showMiniMap="miniMapVisible"
          @contextmenu.capture.prevent="onContextMenu"
        >
          <MNodeList 
            v-if="enableContextMenuNodeList"
            :nodeManager="nodeManager" 
            v-model:nodeListShow="nodeListShow"
            :style="{ top: nodeListPos.y + 'px', left: nodeListPos.x + 'px' }" 
            @position-update="onPanelPositionUpdate"
          />
        </MFlow>
      </div>

      <!-- Right Panel -->
      <RightPanel :visible="rightVisible" @toggle="onRightPanelClose" />
      
      <!-- Bottom Panel -->
      <BottomPanel
        :visible="bottomVisible"
        @update:height="onBottomPanelHeightUpdate"
        :leftOffset="leftVisible ? leftPanelWidth : 0"
        :rightOffset="rightVisible ? rightPanelWidth : 0"
      />
    </div>
  </ElConfigProvider>
</template>

<script lang="ts" setup>
// 组件级样式引入 - 确保样式随组件加载
// import '@/styles/main.scss'

import { ref, computed, onMounted, onUnmounted, nextTick, watch, type PropType, provide } from 'vue'
import { ElConfigProvider } from '@/plugins/elementPlus'
import { MFlow, MNodeList } from '@/components/flow'
import LeftPanel from '@/components/layout/LeftPanel.vue'
import RightPanel from '@/components/layout/RightPanel.vue'
import BottomPanel from '@/components/layout/BottomPanel.vue'
import TopPanel from '@/components/layout/TopPanel.vue'
import { themeManager as defaultThemeManager } from '@/components/theme'
import { useWorkflowStore } from 'mflows'
import { NodeManager as defaultNodeManager } from '@/components/flow'
import { darkTheme } from '@/components/theme/defaultThemes'
import { MFlowInjectionKey, type MFlowAPI } from '@/api/mflow'
import { dependencyState, addDependency, deleteDependency, getDependencies, loadDependency, getLoadedDependency, createFunctionContext } from 'mflows'
import { getGlobalVariables, getWorkflowVariables, addGlobalVariable, addWorkflowVariable, updateGlobalVariable, updateWorkflowVariable, deleteGlobalVariable, deleteWorkflowVariable } from 'mflows'
import { getResources, addResourceFromFile, deleteResource, createJsonResource } from 'mflows'
// removed: FlowRunner import

// Toolbar icons -> use LazyIcon instead of direct PNGs
import { LazyIcon } from '@/exports/components'
// removed: executeIcon import

// Props - 只保留基本的编辑器行为配置
const props = defineProps({
  // 面板显隐控制（支持 v-model）
  leftPanelVisible: { type: Boolean as PropType<boolean>, default: true },
  rightPanelVisible: { type: Boolean as PropType<boolean>, default: false },
  bottomPanelVisible: { type: Boolean as PropType<boolean>, default: true },

  // 编辑器行为开关
  showToolbar: { type: Boolean as PropType<boolean>, default: true },
  enableContextMenuNodeList: { type: Boolean as PropType<boolean>, default: true },

  // 画布参数
  backgroundGap: { type: Number as PropType<number>, default: 22 },
  maxZoom: { type: Number as PropType<number>, default: 1.5 },
  minZoom: { type: Number as PropType<number>, default: 0.5 }
})

const emit = defineEmits<{
  (e: 'update:leftPanelVisible', v: boolean): void
  (e: 'update:rightPanelVisible', v: boolean): void
  (e: 'update:bottomPanelVisible', v: boolean): void
}>()

// 内部管理器和存储 - 不再从外部注入
const nodeManager = defaultNodeManager
const themeManager = defaultThemeManager
const workflowStore = useWorkflowStore()

// 确保默认主题可用
try {
  const current = themeManager?.getCurrentTheme?.()
  if (!current) {
    themeManager?.registerTheme?.(darkTheme)
    themeManager?.switchTheme?.('dark')
  }
} catch (e) {
  // 忽略：不同实现可能没有这些方法
}

// 面板显隐状态
const leftVisible = ref<boolean>(props.leftPanelVisible)
const rightVisible = ref<boolean>(props.rightPanelVisible)
const bottomVisible = ref<boolean>(props.bottomPanelVisible)

// 新增：小地图显隐状态（与右侧面板解耦合）
const miniMapVisible = ref<boolean>(true)

watch(() => props.leftPanelVisible, v => (leftVisible.value = v))
watch(() => props.rightPanelVisible, v => (rightVisible.value = v))
watch(() => props.bottomPanelVisible, v => (bottomVisible.value = v))

// 面板尺寸 - 固定值，不再从 props 传入
const leftPanelWidth = 290
const rightPanelWidth = 321 // 与 RightPanel 实际宽度保持一致
const bottomPanelHeight = ref<number>(240)

function toggleLeft() {
  leftVisible.value = !leftVisible.value
  emit('update:leftPanelVisible', leftVisible.value)
}

// 右侧面板开关保持原样（不再与小地图绑定）
function toggleRight() {
  rightVisible.value = !rightVisible.value
  emit('update:rightPanelVisible', rightVisible.value)
}

// 新增：仅切换小地图显示
function toggleMiniMap() {
  miniMapVisible.value = !miniMapVisible.value
}

// 当 RightPanel 发出 toggle（确认/取消）时，显式关闭面板并同步 v-model
function onRightPanelClose() {
  if (rightVisible.value) {
    rightVisible.value = false
    emit('update:rightPanelVisible', false)
  }
}

function toggleBottom() {
  bottomVisible.value = !bottomVisible.value
  emit('update:bottomPanelVisible', bottomVisible.value)
}

function onBottomPanelHeightUpdate(h: number) {
  bottomPanelHeight.value = h
}

// 新增：双击节点时显示右侧面板
function onNodeDoubleClick(_evt: any) {
  if (!rightVisible.value) {
    rightVisible.value = true
    emit('update:rightPanelVisible', true)
  }
}

// 将编辑器能力通过依赖注入对外暴露，便于自定义节点与外部扩展访问
const mflowApi: MFlowAPI = {
  workflow: workflowStore,
  dependencies: {
    state: dependencyState,
    addDependency,
    deleteDependency,
    getDependencies,
    loadDependency,
    getLoadedDependency,
    createFunctionContext
  },
  variables: {
    getGlobalVariables,
    getWorkflowVariables,
    addGlobalVariable,
    addWorkflowVariable,
    updateGlobalVariable,
    updateWorkflowVariable,
    deleteGlobalVariable,
    deleteWorkflowVariable
  },
  resources: {
    getResources,
    addResourceFromFile,
    deleteResource,
    createJsonResource
  },
  ui: {
    getLeftVisible: () => leftVisible.value,
    setLeftVisible: (v: boolean) => { leftVisible.value = v; emit('update:leftPanelVisible', v) },
    toggleLeft,
    getRightVisible: () => rightVisible.value,
    setRightVisible: (v: boolean) => { rightVisible.value = v; emit('update:rightPanelVisible', v) },
    toggleRight,
    getBottomVisible: () => bottomVisible.value,
    setBottomVisible: (v: boolean) => { bottomVisible.value = v; emit('update:bottomPanelVisible', v) },
    toggleBottom
  },
  nodeManager,
  themeManager
}

provide(MFlowInjectionKey, mflowApi)

// NodeList overlay state
const nodeListShow = ref(false)
const nodeListPos = ref({ x: 20, y: 20 })

function onContextMenu(e: MouseEvent) {
  if (!props.enableContextMenuNodeList) return
  e.preventDefault()

  const panelWidth = 300
  const margin = 10
  const safeOffset = 16

  function computePosition(mouseX: number, mouseY: number, width: number, height: number) {
    const canRight = mouseX + safeOffset + width <= window.innerWidth - margin
    const rightX = mouseX + safeOffset
    const leftX = mouseX - safeOffset - width
    let x = canRight ? rightX : leftX

    const canBottom = mouseY + safeOffset + height <= window.innerHeight - margin
    const canTop = mouseY - safeOffset - height >= margin
    const belowY = mouseY + safeOffset
    const aboveY = mouseY - safeOffset - height

    let y: number
    if (canBottom) {
      y = belowY
    } else if (canTop) {
      y = aboveY
    } else {
      y = mouseY - height / 2
    }

    x = Math.max(margin, Math.min(x, window.innerWidth - width - margin))
    y = Math.max(margin, Math.min(y, window.innerHeight - height - margin))
    return { x, y }
  }

  const estimatedHeight = Math.min(window.innerHeight * 0.6, 420)
  nodeListPos.value = computePosition(e.clientX, e.clientY, panelWidth, estimatedHeight)
  nodeListShow.value = true

  nextTick(() => {
    const boxEl = document.querySelector('.node-box') as HTMLElement | null
    if (boxEl) {
      const rect = boxEl.getBoundingClientRect()
      const pos = computePosition(e.clientX, e.clientY, rect.width, rect.height)
      nodeListPos.value = pos
    }
    const searchInput = document.querySelector('.node-search input') as HTMLInputElement | null
    if (searchInput) searchInput.focus()
  })
}

function onPanelPositionUpdate(newPos: { x: number, y: number }) {
  nodeListPos.value = newPos
}

function onPointerDownOutside(e: PointerEvent) {
  if (!nodeListShow.value) return
  const nodeListEl = document.querySelector('.node-box') as HTMLElement | null
  if (!nodeListEl) return
  const target = e.target as Node
  if (!nodeListEl.contains(target)) {
    nodeListShow.value = false
  }
}

function onKeyDown(e: KeyboardEvent) {
  if (e.key === 'Escape' && nodeListShow.value) {
    nodeListShow.value = false
  }
}

onMounted(() => {
  const anyDoc = document as any
  anyDoc.addEventListener('pointerdown', onPointerDownOutside as any, true)
  document.addEventListener('keydown', onKeyDown)
})

onUnmounted(() => {
  const anyDoc = document as any
  anyDoc.removeEventListener('pointerdown', onPointerDownOutside as any, true)
  document.removeEventListener('keydown', onKeyDown)
})

const mainContentStyle = computed(() => {
  return {
    paddingLeft: leftVisible.value ? leftPanelWidth + 'px' : '0px',
    paddingRight: rightVisible.value ? rightPanelWidth + 'px' : '0px',
    paddingBottom: bottomVisible.value ? bottomPanelHeight.value + 'px' : '0px',
  }
})
</script>

<style lang="scss" scoped>
.app-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
  /* override global .mflow-editor white background to avoid top white strip */
  background: transparent;
}

.main-content {
  position: relative;
  flex: 1;
  overflow: hidden;
  /* remove top margin that created a visible blank strip */
  /* margin-top: 10px; */
}
</style>
